import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import clsx from 'clsx';
import { useState, type CSSProperties, type ReactNode } from 'react';
import CollapsibleCard, { type CollapsibleCardProps } from './CollapsibleCard';

export type AccordionExpandMode = 'single' | 'multiple';

export interface AccordionCardItem {
    id: string;
    title: ReactNode;
    children?: ReactNode;
    meta?: ReactNode;
    actions?: ReactNode;
    className?: string;
    cardClasses?: string;
    /** Applied to the card root (e.g. CSS variables for custom header colors). */
    style?: CSSProperties;
    /** DOM id on the card root, for scrolling back to this section. */
    htmlId?: string;
}

export interface AccordionCardProps {
    items: AccordionCardItem[];
    /**
     * `single` — at most one section open.
     * `multiple` — any number of sections may be open.
     */
    expandMode?: AccordionExpandMode;
    /** Header style passed to each section. */
    variant?: CollapsibleCardProps['variant'];
    /** Corner radius passed to each section. */
    corners?: CollapsibleCardProps['corners'];
    /** Uncontrolled initial open section ids. */
    defaultExpandedIds?: string[];
    /** Controlled open section ids. */
    expandedIds?: string[];
    onExpandedIdsChange?: (expandedIds: string[]) => void;
    /**
     * When `expandMode` is `single`, whether clicking the open section can close it.
     * Defaults to `true`.
     */
    allowAllCollapsed?: boolean;
    /** Enables drag-reorder handles on each section. Requires `onReorder`. */
    draggable?: boolean;
    /** Called with the new item id order after a drag completes. */
    onReorder?: (orderedIds: string[]) => void;
    className?: string;
}

const normalizeDefaultIds = (
    ids: string[] | undefined,
    expandMode: AccordionExpandMode,
    itemIds: string[],
): string[] => {
    const valid = (ids ?? []).filter((id) => itemIds.includes(id));
    if (expandMode === 'single') {
        return valid.slice(0, 1);
    }
    return valid;
};

const AccordionCard = ({
    items,
    expandMode = 'single',
    variant = 'default',
    corners = 'rounded',
    defaultExpandedIds,
    expandedIds: expandedIdsProp,
    onExpandedIdsChange,
    allowAllCollapsed = true,
    draggable = false,
    onReorder,
    className,
}: AccordionCardProps) => {
    const itemIds = items.map((item) => item.id);
    const isControlled = expandedIdsProp !== undefined;
    const [uncontrolledExpandedIds, setUncontrolledExpandedIds] = useState<string[]>(() =>
        normalizeDefaultIds(defaultExpandedIds, expandMode, itemIds),
    );

    const expandedIds = isControlled
        ? normalizeDefaultIds(expandedIdsProp, expandMode, itemIds)
        : uncontrolledExpandedIds;

    const setExpandedIds = (next: string[]) => {
        const normalized = normalizeDefaultIds(next, expandMode, itemIds);
        if (!isControlled) {
            setUncontrolledExpandedIds(normalized);
        }
        onExpandedIdsChange?.(normalized);
    };

    const handleExpandedChange = (id: string, expanded: boolean) => {
        if (expandMode === 'single') {
            if (expanded) {
                setExpandedIds([id]);
                return;
            }
            if (allowAllCollapsed) {
                setExpandedIds([]);
            }
            return;
        }

        if (expanded) {
            setExpandedIds(expandedIds.includes(id) ? expandedIds : [...expandedIds, id]);
            return;
        }
        setExpandedIds(expandedIds.filter((openId) => openId !== id));
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !onReorder) return;

        const oldIndex = itemIds.indexOf(String(active.id));
        const newIndex = itemIds.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;

        onReorder(arrayMove(itemIds, oldIndex, newIndex));
    };

    const sections = items.map((item) => (
        <CollapsibleCard
            key={item.id}
            id={item.id}
            title={item.title}
            meta={item.meta}
            actions={item.actions}
            variant={variant}
            corners={corners}
            className={item.className}
            cardClasses={item.cardClasses}
            style={item.style}
            htmlId={item.htmlId}
            draggable={draggable}
            expanded={expandedIds.includes(item.id)}
            onExpandedChange={(expanded) => handleExpandedChange(item.id, expanded)}
        >
            {item.children}
        </CollapsibleCard>
    ));

    const rootClassName = clsx('accordion-card', className);

    if (draggable) {
        if (process.env.NODE_ENV !== 'production' && !onReorder) {
            console.warn('AccordionCard: `onReorder` is recommended when `draggable` is true.');
        }

        return (
            <div className={rootClassName} data-expand-mode={expandMode}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                        {sections}
                    </SortableContext>
                </DndContext>
            </div>
        );
    }

    return (
        <div className={rootClassName} data-expand-mode={expandMode}>
            {sections}
        </div>
    );
};

export default AccordionCard;
